"use client";

import { useEffect, useRef, useState } from "react";
import type { ArtefactMeta, Verification } from "@/lib/artefact";
import { flashCite } from "@/components/page/citeFlash";

type Props = {
  meta: ArtefactMeta;
  /**
   * Figure-scoped id used to address individual source entries from a
   * matching `<Cite figureId={…} n={n} />` marker in body / derivation
   * prose. Pages that stack several sidenotes (e.g. /climate/hazards)
   * pass distinct ids so each figure's `[1]` resolves to its own
   * sidenote entry. When omitted, anchor ids are not emitted —
   * suitable for figures whose sources aren't referenced inline.
   */
  figureId?: string;
  /**
   * When the source list is longer than `collapseAfter` entries, render
   * only the first N and add a "show all" toggle. Default is
   * `undefined` — no collapse, preserving the existing behaviour on
   * pages that cite only a handful of sources. /council figures pass
   * a small number (e.g. 4) because each artefact cites every eScribe
   * meeting it touched, which can run to a dozen URLs per card.
   */
  collapseAfter?: number;
};

// Lookups accept the audience-facing alias `"machine-ingested"` in
// addition to the canonical `"munidata-pipeline"` enum value, so a
// caller that needs to strip the upstream-pipeline name from the React
// hydration payload (per Constitution v1.3.0 Principle VII) can pass
// the alias as `meta.verification` and still get correct labelling.
type VerificationOrAlias = Verification | "machine-ingested";

const VERIFICATION_LABEL: Record<VerificationOrAlias, string> = {
  "primary-verified": "primary-verified",
  "press-quote": "press quote",
  "automated-extraction": "auto-extracted",
  "munidata-pipeline": "machine-ingested",
  "machine-ingested": "machine-ingested",
  "derived-allocation": "modelled estimate",
  unverified: "unverified",
};

// Translate the canonical tier identifier into the rendered `data-state`
// slug. Mirrors SourceCite — the `"munidata-pipeline"` enum value MUST
// NOT reach DOM; the chip exposes `"machine-ingested"` instead.
const VERIFICATION_DATA_STATE: Record<VerificationOrAlias, string> = {
  "primary-verified": "primary-verified",
  "press-quote": "press-quote",
  "automated-extraction": "automated-extraction",
  "munidata-pipeline": "machine-ingested",
  "machine-ingested": "machine-ingested",
  "derived-allocation": "derived-allocation",
  unverified: "unverified",
};

const VERIFICATION_GLOSS: Record<VerificationOrAlias, string> = {
  "primary-verified":
    "Every figure here was confirmed verbatim against the primary documents below.",
  "press-quote":
    "Sourced from a press paraphrase of a primary speaker; no primary transcript is publicly available.",
  "automated-extraction":
    "Produced by a reproducible automated extraction pipeline from the cited source.",
  "munidata-pipeline":
    "Bulk-ingested at build time from the cited primary source. The URL was fetched and hashed, but its contents have not yet been opened by a human reader.",
  "machine-ingested":
    "Bulk-ingested at build time from the cited primary source. The URL was fetched and hashed, but its contents have not yet been opened by a human reader.",
  "derived-allocation":
    "Modelled estimate produced by a documented allocation formula over primary-verified inputs; the transformation is reproducible from the artefact alone.",
  unverified:
    "Not verified against a primary source — should not be on the site.",
};

const VERIFICATION_DESCRIPTION: Record<VerificationOrAlias, string> = {
  "primary-verified":
    "Every figure on this chart was confirmed verbatim against the cited primary source.",
  "press-quote":
    "Figures here come from a press paraphrase of a primary speaker; no primary transcript is publicly available. Treat with appropriate caution.",
  "automated-extraction":
    "Figures here were produced by an automated extraction pipeline (Gemini PDF→JSON) from the cited source. The extraction is reproducible from the source hash but has not been independently re-verified by a human.",
  "munidata-pipeline":
    "Structural data was machine-ingested at build time from the cited primary source. The source URL was fetched and hashed, but its contents have not yet been opened by a human reader and cross-checked against the artefact. Treat individual figures with appropriate caution; a hand-verified pass will follow.",
  "machine-ingested":
    "Structural data was machine-ingested at build time from the cited primary source. The source URL was fetched and hashed, but its contents have not yet been opened by a human reader and cross-checked against the artefact. Treat individual figures with appropriate caution; a hand-verified pass will follow.",
  "derived-allocation":
    "Figures here are a modelled estimate, not an official measurement. A documented formula allocates citywide totals across smaller units using cited driver datasets. Every input source meets the primary-verified standard, and the allocation is reproducible from this artefact alone — see the methodology page for the exact transformation, its limitations, and how to re-derive any figure.",
  unverified:
    "This artefact has not been verified against a primary source and should not be on the site.",
};

/**
 * Tufte-style sidenote citation rail. Stacks vertically in a narrow
 * margin column alongside the figure it cites; sticks under the header
 * so it stays visible while the reader scrolls past a tall figure.
 *
 * Each source is numbered so the artefact's `meta.derivation` can refer
 * to specific entries (e.g. "as reported in [1]") instead of mashing
 * provenance into one paragraph.
 */
export function SourceSidenote({ meta, figureId, collapseAfter }: Props) {
  const verification: VerificationOrAlias =
    (meta.verification as VerificationOrAlias | undefined) ?? "unverified";
  const asideRef = useRef<HTMLElement>(null);
  const canCollapse =
    typeof collapseAfter === "number" &&
    collapseAfter > 0 &&
    meta.sources.length > collapseAfter;
  const [expanded, setExpanded] = useState(false);
  const visibleCount = canCollapse && !expanded ? collapseAfter : meta.sources.length;
  const hiddenCount = meta.sources.length - visibleCount;

  // Direct hash navigation (page loaded with #cite-{figureId}-{n}, or an
  // external link followed). Routes the deep-link arrival through the
  // same flashCite() helper as in-page <Cite> clicks so the visual is
  // identical — and so reduced-motion users get a transient highlight
  // rather than a CSS :target wash that would persist as long as the
  // URL hash matches.
  useEffect(() => {
    if (!figureId) return;
    const prefix = `cite-${figureId}-`;
    const handle = () => {
      const hash = window.location.hash.slice(1);
      if (!hash.startsWith(prefix)) return;
      const target = document.getElementById(hash);
      if (target) {
        flashCite(target, { scroll: true });
        return;
      }
      // The cite is hidden because `collapseAfter` is in effect and the
      // requested index lives past the visible window. Expand and then
      // retry on the next paint so flashCite has a real element to
      // target — without this, deep-linking to [cite #10] from a body
      // <Cite> marker silently fails.
      const n = parseInt(hash.slice(prefix.length), 10);
      if (!Number.isFinite(n)) return;
      if (canCollapse && !expanded && n > (collapseAfter ?? 0)) {
        setExpanded(true);
        requestAnimationFrame(() => {
          const t = document.getElementById(hash);
          if (t) flashCite(t, { scroll: true });
        });
      }
    };
    handle();
    window.addEventListener("hashchange", handle);
    return () => window.removeEventListener("hashchange", handle);
  }, [figureId, canCollapse, expanded, collapseAfter]);

  useEffect(() => {
    const aside = asideRef.current;
    if (!aside) return;
    // Find the next .cs-sidenote sibling once. If none, the fade is a
    // no-op for this card's lifetime — skip the effect entirely so we
    // never attach scroll/resize listeners on the vast majority of pages
    // (single-sidenote columns).
    let next: Element | null = aside.nextElementSibling;
    while (next && !next.classList.contains("cs-sidenote")) {
      next = next.nextElementSibling;
    }
    if (!next) return;
    const nextEl = next as HTMLElement;
    const mql = window.matchMedia("(min-width: 1024px)");

    // Width of the soft gradient band, in px. The fade isn't uniform —
    // we sweep a soft edge upward through the card so the bottom (the
    // part the incoming card is about to cover) clears first while the
    // top stays solid until later in the scroll.
    const BAND_PX = 96;

    let raf = 0;
    let attached = false;

    const clear = () => {
      aside.style.maskImage = "";
      aside.style.webkitMaskImage = "";
    };

    const update = () => {
      raf = 0;
      const myRect = aside.getBoundingClientRect();
      const nextRect = nextEl.getBoundingClientRect();
      // Overlap distance: positive once the next card has moved up into us.
      // Drives the mask edge upward across the card; by the time the next
      // card fully covers, the mask has swept past the top so the card is
      // entirely transparent — the reader's eye is handed off without a pop.
      const overlap = myRect.bottom - nextRect.top;
      const cardH = Math.max(myRect.height, 1);
      const fade = Math.max(0, Math.min(1, overlap / cardH));
      if (fade <= 0) {
        clear();
        return;
      }
      // edge position from the bottom, in px. Starts below the card so
      // the gradient band hasn't entered yet at fade=0; ends above the
      // top at fade=1 so the entire card is past the transparent stop.
      const edge = fade * (cardH + BAND_PX) - BAND_PX;
      const mask = `linear-gradient(to top, transparent ${edge}px, black ${edge + BAND_PX}px)`;
      aside.style.maskImage = mask;
      aside.style.webkitMaskImage = mask;
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };

    const attach = () => {
      if (attached) return;
      attached = true;
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);
      update();
    };

    const detach = () => {
      if (!attached) return;
      attached = false;
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      clear();
    };

    const onMqlChange = () => {
      if (mql.matches) attach();
      else detach();
    };

    if (mql.matches) attach();
    mql.addEventListener("change", onMqlChange);
    return () => {
      detach();
      mql.removeEventListener("change", onMqlChange);
    };
  }, []);

  return (
    <aside
      ref={asideRef}
      className="cs-sidenote cs-card-paper bg-bg rounded-2xl p-5 sm:p-6 text-sm"
      aria-label={`Citations for ${meta.title}`}
    >
      <div className="cs-label mb-2">verification</div>
      <span
        className="cs-verify-chip"
        data-state={VERIFICATION_DATA_STATE[verification]}
        title={VERIFICATION_DESCRIPTION[verification]}
      >
        {VERIFICATION_LABEL[verification]}
      </span>
      <p className="mt-2 text-[13px] leading-relaxed text-ink-3">
        {VERIFICATION_GLOSS[verification]}
      </p>

      <div className="cs-label mt-6 mb-2">
        {meta.sources.length > 1 ? "sources" : "source"}
      </div>
      <ol className="list-none p-0 space-y-4">
        {meta.sources.slice(0, visibleCount).map((s, i) => {
          const isPointer = s.verified_by === "context-pointer";
          const anchorId = figureId ? `cite-${figureId}-${i + 1}` : undefined;
          return (
            <li
              key={`${i}-${s.url}`}
              id={anchorId}
              tabIndex={anchorId ? -1 : undefined}
              className={`flex gap-2 ${anchorId ? "cs-cite-target" : ""}`}
            >
              <span className="cs-num text-ink-3 flex-none w-5 pt-[2px] text-[13px]">
                [{i + 1}]
              </span>
              <div className="min-w-0 flex-1">
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`block leading-snug hover:text-bow ${
                    isPointer
                      ? "text-ink-3 text-[13px]"
                      : "text-ink text-[14px] font-medium"
                  }`}
                >
                  {s.title}
                </a>
                <div className="mt-0.5 text-ink-2 text-[12px] leading-snug">
                  {s.publisher}
                </div>
                {s.verified_at && !isPointer && (
                  <div className="mt-0.5 cs-mono text-ink-3 text-[11px] tracking-wide italic">
                    verified {s.verified_at}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {canCollapse && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="cs-mono mt-3 text-[12px] text-ink-3 hover:text-ink underline-offset-2 hover:underline"
        >
          {expanded
            ? "↑ collapse"
            : `↓ show all ${meta.sources.length} (${hiddenCount} more)`}
        </button>
      )}

      <details className="mt-6 group">
        <summary className="cs-label cursor-pointer hover:text-ink-2 list-none">
          derivation
          <span
            aria-hidden
            className="ml-1 inline-block transition-transform group-open:rotate-90"
          >
            ›
          </span>
        </summary>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-2 italic">
          {typeof meta.derivation === "string"
            ? meta.derivation
            : meta.derivation.summary}
        </p>
      </details>
    </aside>
  );
}
